import React from "react";
import {
  CircularProgress,
  Typography,
  Checkbox,
  Paper,
  Skeleton,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { formatCurrency, getProductValues } from "../../helper/constants";
import { useQuoteContext } from "../../shared/contexts/QuoteContext";
import { Package, Product } from "../../shared/types/quotes";

/**
 * Component for displaying and managing quote summary products and packages.
 *
 * @param {Object} props - The component's props.
 * @param {Function} props.checkBoxHandler - A function to handle checkbox changes.
 * @returns {JSX.Element} - React JSX element representing the summary of quote products and packages
 */
const QuoteSummaryProductsTable = (props: any) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.up("sm"));

  const { currentQuotes, decisionTree, loadingProducts, quoteCheckbox } = useQuoteContext();

  const formik = props.options;
  const showCheckbox = props.showCheckbox ?? true;

  const checkboxSX = {
    color: "#474848",
    "&.Mui-checked": {
      color: "#474848"
    },
    "&.Mui-disabled": {
      color: "rgba(182, 182, 182, 1)",
      pointerEvents: "auto",
      "&:hover": {
        backgroundColor: "transparent"
      },
      cursor: "not-allowed",
      "& .MuiSvgIcon-root .innerBox": {
        fill: "#eee"
      }
    }
  };

  const renderProductRow = (
    item: Product,
    checkboxProps: { checked?: boolean; disabled: boolean; onClick?: any }
  ) => {
    const { excess, liability, premium } = getProductValues(
      item,
      currentQuotes,
      decisionTree
    );

    return (
        <TableRow key={item?.id}>
            {checkboxProps.checked !== undefined &&
                checkboxProps.onClick !== undefined && (
                    <TableCell>
                    <Checkbox
                        sx={checkboxSX}
                        checked={checkboxProps?.checked}
                        disabled={checkboxProps?.disabled}
                        onClick={checkboxProps?.onClick}
                        size="small"
                    />
                    </TableCell>
                )}
            
            <TableCell width={"25%"}>{item?.name}</TableCell>
            <TableCell width={"25%"}>
            {loadingProducts ? (
                <Skeleton sx={{ maxWidth: "3em" }} />
            ) : (
                formatCurrency(liability).split(".")[0]
            )}
            </TableCell>
            <TableCell width={"25%"}>
            {loadingProducts ? (
                <Skeleton sx={{ maxWidth: "3em" }} />
            ) : (
                formatCurrency(excess).split(".")[0]
            )}
            </TableCell>
            <TableCell width={"25%"}>
            {loadingProducts ? (
                <Skeleton sx={{ maxWidth: "3em" }} />
            ) : premium != null && typeof premium == "string" ? (
                premium
            ) : (
                formatCurrency(premium)
            )}
            </TableCell>
        </TableRow>
    );
  };

  return (
      <TableContainer>
      <Table>
          <TableHead>
                <TableRow>
                    <TableCell colSpan={showCheckbox ? 2 : 1} width={"25%"}/>
                    <TableCell width={"25%"}>Limit</TableCell>
                    <TableCell width={"25%"}>Retention</TableCell>
                    <TableCell width={"25%"}>Premium</TableCell>
                </TableRow>
            </TableHead>
        <TableBody>
          {formik.values.products.map((item: Product) => {
            let locked =
              formik.values.locked.products.find(
                (id: any) => id == item.id
              ) != null;

            return renderProductRow(item, {
              ...(showCheckbox && {
                checked:
                  quoteCheckbox.find(
                    (x: any) => x.id === item.id && x.packageId === "PRODUCT"
                  ) == null
              }),
              disabled:
                loadingProducts ||
                (item?.isMainForPackage ?? false) ||
                locked,
              ...(props.checkBoxHandler && {
                onClick: () => props.checkBoxHandler(item?.id, "PRODUCT")
              })
            });
          })}
          {formik.values.packages.map(
            (packageItem: Package) => {
              let locked =
                formik.values.locked.packages.find(
                  (id: any) => id == packageItem.id
                ) != null;
              let products =
                packageItem.products;
              return (
                <React.Fragment key={packageItem?.id}>
                  <TableRow>
                    <TableCell colSpan={showCheckbox ? 5 : 4}>
                      <Typography variant="subtitle1">
                        {packageItem?.name}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {products?.map(product => {
                    return renderProductRow(product, {
                      ...(showCheckbox && {
                        checked:
                          quoteCheckbox.find(
                            (x: any) =>
                              x.id === product?.id &&
                              x.packageId === packageItem?.id
                          ) == null
                      }),
                      disabled:
                        loadingProducts ||
                        (product?.isMainForPackage ?? false) ||
                        locked,
                      ...(props.checkBoxHandler && {
                        onClick: () =>
                          props.checkBoxHandler(product?.id, packageItem?.id)
                      })
                    });
                  })}
                </React.Fragment>
              );
            }
          )}
        </TableBody>
      </Table>
      </TableContainer>
  );
};

export default QuoteSummaryProductsTable;